/**
 * statistics.js — Phase 5 Intelligence Layer primitives.
 *
 * Minimal, dependency-free statistical helpers used by the projection
 * functions. Pure; no DB, no side effects, no randomness (deterministic).
 *
 * Design notes for auditors:
 *   - No stats library is in package.json. These are hand-rolled and
 *     covered by unit tests against known distributions.
 *   - Percentile uses R-7 linear interpolation (same as NumPy default).
 *   - Variance uses n-1 denominator (sample variance, unbiased).
 *   - Gaussian band assumes symmetric distribution. For non-negative
 *     quantities (throughput, cost, time), the caller MUST truncate
 *     the low bound at 0 via truncate().
 *   - Band confidence is a free parameter: PROJECTION_BAND_Z maps
 *     a z-score to a confidence level (1.282 → 80%, 1.645 → 90%,
 *     1.96 → 95%). v1 uses 80%; constants live in src/constants/enums.js.
 *
 * When sample size is too small for a stable band, caller uses
 * `insufficientData()` helper (in projections/confidenceBand.js).
 */

/**
 * Arithmetic mean. Returns null on empty input (don't silently return 0).
 * @param {number[]} samples
 * @returns {number|null}
 */
export function mean(samples) {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  let sum = 0;
  for (const v of samples) {
    if (!Number.isFinite(v)) {
      throw new Error(`statistics.mean: non-finite sample ${v}`);
    }
    sum += v;
  }
  return sum / samples.length;
}

/**
 * Sample standard deviation (n-1 denominator). Returns null for n < 2
 * (single-sample stdev is undefined, not zero).
 * @param {number[]} samples
 * @returns {number|null}
 */
export function stdev(samples) {
  if (!Array.isArray(samples) || samples.length < 2) return null;
  const m = mean(samples);
  let sumSq = 0;
  for (const v of samples) sumSq += (v - m) * (v - m);
  return Math.sqrt(sumSq / (samples.length - 1));
}

/**
 * Linear interpolation percentile (NumPy R-7 / SQL PERCENTILE_CONT).
 * percentile(samples, 0.5) → median.
 *
 * @param {number[]} samples
 * @param {number} p in [0, 1]
 * @returns {number|null}
 */
export function percentile(samples, p) {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  if (!(p >= 0 && p <= 1)) throw new Error(`statistics.percentile: p must be in [0,1], got ${p}`);
  const sorted = [...samples].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Student-t critical values for an 80% symmetric band (α/2 = 0.10).
 *
 * Phase 5.5 R3 audit fix (H2a — statistical honesty):
 *   Previous code used z = 1.282 (Gaussian quantile) regardless of sample
 *   size. That implicitly assumes population σ is known, which is false
 *   when σ is estimated from the sample itself. For small n, the correct
 *   distribution is Student's t, and t₀.₁₀,df can be 30-50% wider than z.
 *
 * Values below are from the standard Student-t table at upper-tail 0.10
 * (two-sided 80% interval). For df ≥ 30 the difference from z is <2%; we
 * fall back to z at that point. Degrees of freedom = samples - 1.
 */
const STUDENT_T_80 = {
  1: 3.078, 2: 1.886, 3: 1.638, 4: 1.533, 5: 1.476,
  6: 1.440, 7: 1.415, 8: 1.397, 9: 1.383, 10: 1.372,
  11: 1.363, 12: 1.356, 13: 1.350, 14: 1.345, 15: 1.341,
  16: 1.337, 17: 1.333, 18: 1.330, 19: 1.328, 20: 1.325,
  21: 1.323, 22: 1.321, 23: 1.319, 24: 1.318, 25: 1.316,
  26: 1.315, 27: 1.314, 28: 1.313, 29: 1.311,
};

/**
 * Map (n, bandZ) → effective critical value.
 *   - When bandZ is approximately 1.282 (the standard 80% z-value), we
 *     apply the Student-t correction from the table above.
 *   - For other bandZ values (e.g. a custom 90% z=1.645) we pass z through
 *     unchanged — we only have a Student-t table for the 80% band today.
 *     The 80% band is the product's nominal choice per PROJECTION_BAND_Z
 *     in src/constants/enums.js.
 *
 * Returns a number suitable for `critValue × stdev × √N` band math.
 */
/**
 * R12 MED-1 audit fix: export so `sigmaFromBand` and other back-computation
 * paths can use the SAME critical value that `gaussianBand`/`projectThroughput`
 * used to build the band. Without this, sigma reconstruction at small n is
 * biased by the ratio `crit / z` (~1.47× at n=3), pulling deadline-hit and
 * cost-overrun probabilities toward the 0.5 midpoint and biasing tier
 * assignments toward "fifty_fifty".
 */
export function effectiveCritValue(sampleSize, bandZ) {
  if (!Number.isFinite(sampleSize) || sampleSize < 1) return bandZ;
  if (sampleSize >= 30) return bandZ;
  // Only apply t-correction for the 80% band.
  if (Math.abs(bandZ - 1.282) > 0.01) return bandZ;
  const df = sampleSize - 1;
  return STUDENT_T_80[df] || bandZ;
}

/**
 * Correlation / variance-inflation factor for summing per-annotator
 * weekly outputs. Per R3 audit (H2b), treating annotators as independent
 * understates sum-variance because same-project annotators share common
 * shocks (tool outages, backlog crunches, client-deadline pressure).
 *
 * Without longitudinal covariance data we can't measure ρ directly, so
 * we apply a conservative multiplicative inflation. 1.2 = assumes a
 * modest level of correlation; documented in projectThroughput as an
 * explicit tunable. Higher → wider bands (more honest), lower → narrower
 * (more aggressive). 1.0 = treat as independent (old behavior, unsafe
 * per audit).
 */
export const CORRELATION_INFLATION_FACTOR = 1.2;

/**
 * Gaussian-approximation (or Student-t corrected) symmetric band around
 * the mean.
 *
 * Phase 5.5 R3 audit notes:
 *   - Student-t correction applied when sampleSize < 30 and the requested
 *     band is the product's nominal 80% band (z ≈ 1.282). See
 *     effectiveCritValue + STUDENT_T_80 above.
 *   - Method label is kept as 'gaussian' for backward compatibility but
 *     the math is honest Student-t for small n. The downstream Projection
 *     still reports method='gaussian' — because we're still assuming a
 *     symmetric point estimate, not a t-distribution of the underlying
 *     quantity. Only the critical value changed.
 *
 * @param {number[]} samples
 * @param {number} z — z-score for desired confidence (e.g., 1.282 for 80%)
 * @returns {{point: number|null, low: number|null, high: number|null, method: string, sampleSize: number}}
 */
export function gaussianBand(samples, z) {
  if (!Array.isArray(samples) || samples.length === 0) {
    return { point: null, low: null, high: null, method: 'gaussian', sampleSize: 0 };
  }
  if (!Number.isFinite(z) || z <= 0) {
    throw new Error(`statistics.gaussianBand: z must be positive, got ${z}`);
  }
  const m = mean(samples);
  const s = stdev(samples);
  if (s === null) {
    // n < 2 — point estimate only, no band.
    return { point: m, low: null, high: null, method: 'gaussian', sampleSize: samples.length };
  }
  const crit = effectiveCritValue(samples.length, z);
  const halfWidth = crit * s / Math.sqrt(samples.length); // standard error of the mean
  return {
    point: m,
    low: m - halfWidth,
    high: m + halfWidth,
    method: 'gaussian',
    sampleSize: samples.length,
  };
}

/**
 * Percentile-based band (non-parametric). Use when sample size is large
 * enough for reliable quantile estimates (n ≥ 30 is the usual heuristic).
 *
 * Returns { point, low, high, method: 'percentile', sampleSize }. point
 * is the median; low/high are pLow/pHigh percentiles.
 *
 * @param {number[]} samples
 * @param {number} pLow — e.g., 0.1 for P10
 * @param {number} pHigh — e.g., 0.9 for P90
 * @returns {{point: number|null, low: number|null, high: number|null, method: string, sampleSize: number}}
 */
export function percentileBand(samples, pLow, pHigh) {
  if (!Array.isArray(samples) || samples.length === 0) {
    return { point: null, low: null, high: null, method: 'percentile', sampleSize: 0 };
  }
  if (!(pLow < pHigh)) throw new Error(`statistics.percentileBand: pLow must be < pHigh`);
  return {
    point: percentile(samples, 0.5),
    low: percentile(samples, pLow),
    high: percentile(samples, pHigh),
    method: 'percentile',
    sampleSize: samples.length,
  };
}

/**
 * Clamp a band's bounds to a range. Non-negative quantities (throughput,
 * ETA, cost) MUST be clamped at 0 or above — a naive Gaussian band can
 * produce negative lower bounds which are nonsensical.
 *
 * Null semantics (L6 audit fix — docstring sharpened):
 *   - Null bounds are PRESERVED as null. A null point/low/high means
 *     "no estimate exists", which is semantically distinct from "estimate
 *     happens to equal min". Clamping null→min would silently manufacture
 *     a fake estimate; instead we leave it null so callers/validators
 *     (confidenceBand.validateProjection) can keep enforcing the
 *     null-symmetry invariant on bands.
 *   - Non-null bounds are clamped into [min, max] inclusive.
 *   - A falsy `band` argument (null/undefined) is returned unchanged —
 *     callers that never built a band keep the null, they don't get a
 *     synthetic empty object.
 *
 * @param {{point, low, high, ...}} band
 * @param {number} min
 * @param {number} [max=Infinity]
 * @returns the same shape with clamped low/high
 */
export function truncate(band, min, max = Infinity) {
  if (!band) return band;
  return {
    ...band,
    point: band.point == null ? null : Math.min(max, Math.max(min, band.point)),
    low:   band.low   == null ? null : Math.min(max, Math.max(min, band.low)),
    high:  band.high  == null ? null : Math.min(max, Math.max(min, band.high)),
  };
}

/**
 * Ratio of two point estimates with propagated variance (first-order
 * Taylor / delta method). Used e.g. for ETA = items_remaining / throughput.
 *
 * Assumes numerator is deterministic (a known target count) and denominator
 * has variance. Returns a band around the ratio.
 *
 * Note: this is a *simplification* — the proper distribution of a
 * constant/random-variable ratio is not symmetric when denominator
 * variance is large. For v1 we use symmetric delta-method bounds,
 * truncated non-negative. Caller should switch to bootstrap when
 * sample size grows and precision matters.
 *
 * @param {number} numerator — known count (no variance)
 * @param {{point, low, high}} denominatorBand
 * @returns {{point, low, high}} (or nulls when denominator is null/0)
 */
/**
 * Standard normal CDF Φ(x) approximation.
 *
 * Phase 5.5 R3 audit addition — needed for deadline-hit / budget-overrun
 * probability. Uses Abramowitz & Stegun 7.1.26 rational approximation of
 * erf() (max error ≈ 7.5e-8, sufficient for UI-tier bucketing).
 *
 * @param {number} x
 * @returns {number} Φ(x) ∈ [0, 1]
 */
export function normalCdf(x) {
  if (!Number.isFinite(x)) {
    if (x === Infinity) return 1;
    if (x === -Infinity) return 0;
    return NaN;
  }
  // erf(x) via A&S 7.1.26
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * absX);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return 0.5 * (1 + sign * erf);
}

/**
 * Given an approximately-normal confidence band [low, point, high] and a
 * reference z-score that was nominally used to construct the band
 * (typically z = 1.282 for 80%), estimate the underlying standard
 * deviation. Half-width = crit × σ, so σ ≈ halfWidth / crit.
 *
 * R12 MED-1 audit fix: when `sampleSize` is provided, use `effectiveCritValue`
 * to match the Student-t correction applied at band-construction time. Without
 * this, σ is inflated by crit/z (~1.47× at n=3, ~1.15× at n=5), shrinking the
 * reconstructed z-score in downstream normal-CDF calls and biasing tier
 * assignments toward "fifty_fifty". When sampleSize is omitted or invalid,
 * falls back to raw bandZ (preserves pre-R12 behavior for deterministic
 * callers that know σ was built with plain z).
 *
 * @param {{point: number, low: number, high: number}} band
 * @param {number} bandZ — nominal z-score (e.g. 1.282)
 * @param {number} [sampleSize] — n of the underlying sample that built the
 *   band; when omitted, the raw bandZ is used (legacy behavior).
 * @returns {number|null} σ estimate, null if band is degenerate
 */
export function sigmaFromBand(band, bandZ, sampleSize) {
  if (!band || band.low == null || band.high == null || band.point == null) return null;
  if (!Number.isFinite(bandZ) || bandZ <= 0) return null;
  const halfWidth = (band.high - band.low) / 2;
  // R3 L2 audit fix: a negative halfWidth (low > high) indicates a
  // malformed band — upstream validateProjection should reject these but
  // defense-in-depth matters. Return null so downstream callers enter
  // their "no variance, fallback" paths rather than silently treating
  // σ=0 as a perfectly-confident point estimate.
  if (halfWidth < 0) return null;
  if (halfWidth === 0) return 0;
  const crit = effectiveCritValue(sampleSize, bandZ);
  return halfWidth / crit;
}

export function ratioBand(numerator, denominatorBand) {
  if (!denominatorBand || denominatorBand.point == null || denominatorBand.point <= 0) {
    return { point: null, low: null, high: null };
  }
  // Point estimate: items / pointThroughput
  const point = numerator / denominatorBand.point;
  // Lower denominator → higher ratio (longer ETA), and vice versa.
  const low  = denominatorBand.high != null && denominatorBand.high > 0
    ? numerator / denominatorBand.high : null;
  const high = denominatorBand.low  != null && denominatorBand.low  > 0
    ? numerator / denominatorBand.low  : null;
  // H1 audit fix: null-symmetry invariant. If either bound is null (e.g.
  // denominator.low was truncated to 0 making high = num/0 = null), we
  // drop BOTH bounds rather than emit a half-null band. Caller decides
  // how to handle (typically: point-only result, downgraded confidence).
  if ((low == null) !== (high == null)) {
    return { point, low: null, high: null };
  }
  return { point, low, high };
}
